import { Component, Inject, OnInit } from '@angular/core';
import { I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { forkJoin, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ButtonDirective, ButtonSpacerDirective } from '../../directives/button.directive';
import { SidenavContentRef } from '../sidenav-content/sidenav-content-ref';
import { SIDENAV_CONTENT_DATA } from '../sidenav-content/sidenav-content.service';
import { FormSubmission, FormSubmissionFlat } from '../../../../../models/entities/forms/form-submission';
import { FormSubmissionStatus } from '../../../../../models/entities/forms/form-submission-status';
import { ViolationStatus } from '../../../../../models/entities/violation-status';
import { ViolationStatement } from '../../../../../models/entities/violation-statement';
import { ViolationHeaderFooter } from '../../../../../models/entities/violation-header-footer';
import { ViolationTemplate } from '../../../../../models/entities/violation-template';
import { RequestFormService } from '../../../../../services/forms/request-form.service';
import { ViolationsService } from '../../../../../services/violations/violations.service';
import { GrowlerService } from '../../../../../services/growler.service';
import { SidenavContentModule } from '../sidenav-content/sidenav-content.module';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MemberAdminDialogComponent, MemberAdminDialogInput, MemberAdminDialogOutput } from '../../../memberships/shared/member-admin-dialog/member-admin-dialog.component';
import { MatDialogSizes } from '../../../../../enums/mat-dialog-sizes.enum';
import { PreventClickDirective } from '../../directives/prevent-click.directive';

export interface StatusChangeSidenavInput
{
    modelType: 'submissions' | 'violations';
    model: FormSubmission | FormSubmissionFlat | ViolationStatement;
    statuses: (FormSubmissionStatus | ViolationStatus)[];
}

export interface StatusChangeSidenavOutput
{
    status: FormSubmissionStatus | ViolationStatus;
    success: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-sidenav-status-change',
    templateUrl: './status-change-sidenav.component.html',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatSlideToggleModule,
        MatDialogModule,
        I18nPluralPipe,
        MatButtonModule,
        ButtonDirective,
        NgIf,
        NgForOf,
        ButtonSpacerDirective,
        SidenavContentModule,
        PreventClickDirective,
    ],
})
export class StatusChangeSidenavComponent implements OnInit
{
    formGroup = this._fb.group({
        currentStatus: '',
        status: [null, Validators.required],
        sendEmail: false,
        recipients: this._fb.nonNullable.control<number[]>([]),
        templates: this._fb.nonNullable.group({
            changeTemplate: false,
            headerTemplate: {value: 0, disabled: true},
            bodyTemplate: {value: 0, disabled: true},
            footerTemplate: {value: 0, disabled: true},
        }),
    });
    recipientsMapping: { [prop: string]: string; } = {
        '=0': 'No recipients',
        '=1': '1 recipient',
        'other': '# recipients',
    };
    selectedStatusId: number;

    violationTemplates: {
        header: ViolationHeaderFooter[];
        body: ViolationTemplate[];
        footer: ViolationHeaderFooter[];
    };

    isSaving = false;

    constructor(
            private _requestFormService: RequestFormService,
            private _violationsService: ViolationsService,
            private _fb: FormBuilder,
            private _growler: GrowlerService,
            private _dialog: MatDialog,
            private _sidenavRef: SidenavContentRef<StatusChangeSidenavComponent>,
            @Inject(SIDENAV_CONTENT_DATA) public data: StatusChangeSidenavInput,
    ) {
    }

    get isSubmissionStatus() {
        return this.data.modelType === 'submissions';
    }

    get isViolationStatus() {
        return this.data.modelType === 'violations';
    }

    get hasSelectedRecipients() {
        return this.formGroup.get('recipients').value?.length > 0;
    }

    ngOnInit() {
        this.formGroup.get('currentStatus').setValue(this.data.model.status);
        this.formGroup.get('currentStatus').disable();

        if (this.data.statuses.length === 0) {
            this.formGroup.get('status').disable();
        }

        if (this.isSubmissionStatus) {
            this.selectedStatusId = (this.data.model as FormSubmission).formSubmissionStatusId;
        } else if (this.isViolationStatus) {
            this.selectedStatusId = (this.data.model as ViolationStatement).violationStatusId;
            forkJoin([
                this._violationsService.getViolationTemplates(this.data.model.organizationId),
                this._violationsService.getViolationHeaderFooters(this.data.model.organizationId),
            ]).pipe(untilDestroyed(this)).subscribe({
                next: ([templates, headerFooters]) => {
                    this.violationTemplates = {
                        header: headerFooters.filter(t => !!t.header) || [],
                        body: templates || [],
                        footer: headerFooters.filter(t => !!t.footer) || [],
                    };
                    if (this.violationTemplates.header.length > 0) {
                        this.formGroup.get('templates.headerTemplate').enable();
                    }
                    if (this.violationTemplates.body.length > 0) {
                        this.formGroup.get('templates.bodyTemplate').enable();
                    }
                    if (this.violationTemplates.footer.length > 0) {
                        this.formGroup.get('templates.footerTemplate').enable();
                    }
                },
                error: () => this._growler.oops('Unable to load violation templates'),
            });
        }

        this.formGroup.get('sendEmail').valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: (value) => {
                value ? this.showNotificationMemberSelector() : this.formGroup.get('recipients').reset();
            },
        });
    }

    showNotificationMemberSelector(): void {
        const data: MemberAdminDialogInput = {
            title: 'Select Recipients',
            required: true,
            hideMissingEmail: true,
            selectedMembershipIds: this.formGroup.get('recipients').value,
            orgId: this.data.model.organizationId,
            unitId: this.data.model.unitId,
            confirmButton: {
                '=0': 'Select Recipients',
                '=1': 'Select 1 Recipient',
                other: 'Select # Recipients',
            },
            emptyStateLabel: 'No recipients so far...',
        };

        this._dialog.open(MemberAdminDialogComponent, {
            width: MatDialogSizes.MD,
            data,
            autoFocus: 'dialog',
            panelClass: ['v2-dialog', 'selector-layout'],
        }).afterClosed().subscribe((result: MemberAdminDialogOutput) => {
            if (result?.success) {
                if (result?.selectedMemberships.length > 0) {
                    this.formGroup.get('recipients').setValue(result.selectedMemberships.map((m) => m.id));
                } else {
                    this.formGroup.get('sendEmail').reset(false);
                }
            } else if (data.selectedMembershipIds.length === 0) {
                this.formGroup.get('sendEmail').reset(false);
            }
        });
    }

    applyStatus() {
        if (this.formGroup.invalid) {
            this._growler.oops('Unable to change status');
            return;
        }

        const status = this.data.statuses.find((s) => {
            return s.id === Number(this.formGroup.get('status').value);
        });

        let obs: Observable<any>;
        switch (this.data.modelType) {
            case 'submissions':
                obs = this._requestFormService.changeFormSubmissionStatuses({
                    formSubmissionStatusId: (status as FormSubmissionStatus)?.id,
                    formSubmissionIds: [this.data.model.id],
                    recipientMemberIds: this.formGroup.get('recipients').value,
                    organizationId: (this.data.model as FormSubmission).form.organizationId,
                });
                break;
            case 'violations':
                obs = this._violationsService.changeViolationStatus({
                    violationStatusId: (status as ViolationStatus)?.id,
                    violationIds: [this.data.model.id],
                    recipientMemberIds: this.formGroup.get('recipients').value,

                    // send 0 if they are not changing the template selections
                    headerTemplateId: this.formGroup.get('templates.headerTemplate').getRawValue(),
                    bodyTemplateId: this.formGroup.get('templates.bodyTemplate').getRawValue(),
                    footerTemplateId: this.formGroup.get('templates.footerTemplate').getRawValue(),
                });
                break;
        }

        if (obs) {
            this._sidenavRef.loading = this.isSaving = true;
            obs.pipe(
                    finalize(() => this._sidenavRef.loading = this.isSaving = false),
                    untilDestroyed(this),
            ).subscribe({
                next: () => {
                    const output: StatusChangeSidenavOutput = {
                        status,
                        success: true,
                    };
                    this._growler.success('Success', 'Status changed.');
                    this._sidenavRef.close(output);
                },
                error: () => this._growler.oops('Unable to update status'),
            });
        }
    }
}
